import {
  CREATE_PACKAGE,
  DELETE_PACKAGE,
  FETCH_PACKAGE_BY_ID,
  FETCH_PACKAGES,
  SEARCH_HUB_CATALOGS,
  SEARCH_HUB_PACKAGES,
  UPDATE_PACKAGE,
} from "@apiUtils";
import {
  mutationFetch,
  type SearchPayload,
  type UseSearchQueryOptions,
  useSearchQuery,
} from "@atlas/sdk";
import type { IHubPackage } from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import {
  HUB_CATALOG_BY_ID_AND_VERSION_KEY,
  HUB_CATALOG_BY_ID_KEY,
  HUB_CATALOGS_KEY,
} from "./HubCatalogs";

export const HUB_PACKAGES_KEY = "hubPackagesKey";
export const HUB_PACKAGE_BY_ID_KEY = "hubPackageByIdKey";

export const getHubPackagesKey = () => [HUB_PACKAGES_KEY] as const;

export const getHubPackageByIdKey = (packageId: number) =>
  [HUB_PACKAGE_BY_ID_KEY, packageId] as const;

export const useHubPackages = (
  options?: Partial<
    UseQueryOptions<
      IHubPackage[],
      AxiosError,
      IHubPackage[],
      ReturnType<typeof getHubPackagesKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubPackagesKey(),
    queryFn: async () => {
      return mutationFetch<IHubPackage[]>({
        url: FETCH_PACKAGES,
        method: "GET",
      });
    },
    ...options,
  });
};

export const useHubPackage = (
  packageId: number,
  options?: Partial<
    UseQueryOptions<
      IHubPackage,
      AxiosError,
      IHubPackage,
      ReturnType<typeof getHubPackageByIdKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getHubPackageByIdKey(packageId),
    queryFn: async () => {
      return mutationFetch<IHubPackage>({
        url: FETCH_PACKAGE_BY_ID.replace(":packageId", String(packageId)),
        method: "GET",
      });
    },
    ...options,
    enabled: (options?.enabled ?? true) && packageId > 0,
  });
};

const createHubPackage = (body: IHubPackage): Promise<IHubPackage> => {
  return mutationFetch<IHubPackage>({
    url: CREATE_PACKAGE,
    method: "POST",
    body,
  });
};

const updateHubPackage = (body: IHubPackage): Promise<IHubPackage> => {
  return mutationFetch<IHubPackage>({
    url: UPDATE_PACKAGE,
    method: "PUT",
    body,
  });
};

const deleteHubPackage = (packageId: number): Promise<void> => {
  return mutationFetch<void>({
    url: DELETE_PACKAGE.replace(":packageId", String(packageId)),
    method: "DELETE",
  });
};

export const useCreateHubPackage = (
  options?: UseMutationOptions<IHubPackage, AxiosError, IHubPackage>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createHubPackage,
    onSuccess: (data: IHubPackage) => {
      queryClient.invalidateQueries({ queryKey: [HUB_PACKAGES_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_PACKAGES] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_BY_ID_KEY] });
      queryClient.invalidateQueries({
        queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY],
      });
      if (data.packageId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubPackageByIdKey(data.packageId),
        });
      }
    },
    ...options,
  });
};

export const useUpdateHubPackage = (
  options?: UseMutationOptions<IHubPackage, AxiosError, IHubPackage>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateHubPackage,
    onSuccess: (data: IHubPackage) => {
      queryClient.invalidateQueries({ queryKey: [HUB_PACKAGES_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_PACKAGES] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_BY_ID_KEY] });
      queryClient.invalidateQueries({
        queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY],
      });
      if (data.packageId != null) {
        queryClient.invalidateQueries({
          queryKey: getHubPackageByIdKey(data.packageId),
        });
      }
    },
    ...options,
  });
};

export const useDeleteHubPackage = (
  options?: UseMutationOptions<void, AxiosError, number>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteHubPackage,
    onSuccess: (_data: unknown, packageId: number) => {
      queryClient.invalidateQueries({ queryKey: [HUB_PACKAGES_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_PACKAGES] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOGS_KEY] });
      queryClient.invalidateQueries({ queryKey: [SEARCH_HUB_CATALOGS] });
      queryClient.invalidateQueries({ queryKey: [HUB_CATALOG_BY_ID_KEY] });
      queryClient.invalidateQueries({
        queryKey: [HUB_CATALOG_BY_ID_AND_VERSION_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: getHubPackageByIdKey(packageId),
      });
    },
    ...options,
  });
};

export const useSearchHubPackages = (
  payload: SearchPayload,
  options?: Omit<UseSearchQueryOptions<IHubPackage>, "url" | "payload">,
) => {
  return useSearchQuery<IHubPackage>({
    url: SEARCH_HUB_PACKAGES,
    payload,
    ...options,
  });
};
